import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  postRequest,
  getRequest,
  putRequest,
} from "../../../../Utilities/ApiHelpers/ApiHelpers.index";
import {
  IPaddleProduct,
  ICreatePaddleProductRequest,
  ICreatePaddlePriceRequest,
  IUpdatePaddleProductRequest,
  IUpdatePaddlePriceRequest,
  IPaddlePrice,
  IFeatureGroup,
  IProductEdition,
} from "./types";
import { ServiceType } from "Config/types";

const BASE_URL = "/api/PaddleProduct";

export const getAllPaddleProducts = createAsyncThunk(
  "paddleProducts/getAll",
  async () => {
    const response = await getRequest(
      `${BASE_URL}/products`,
      null,
      true,
      ServiceType.Payment
    );
    return response.data as IPaddleProduct[];
  }
);

export const getPaddleProduct = createAsyncThunk(
  "paddleProducts/getOne",
  async (productId: string) => {
    const response = await getRequest(
      `${BASE_URL}/products/${productId}`,
      null,
      true,
      ServiceType.Payment
    );
    return response.data as IPaddleProduct;
  }
);

export const createPaddleProduct = createAsyncThunk(
  "paddleProducts/create",
  async (request: ICreatePaddleProductRequest) => {
    const response = await postRequest(
      `${BASE_URL}/products`,
      null,
      true,
      request,
      null,
      ServiceType.Payment
    );
    return response.data as IPaddleProduct;
  }
);

export const updatePaddleProduct = createAsyncThunk(
  "paddleProducts/update",
  async ({
    productId,
    request,
  }: {
    productId: string;
    request: IUpdatePaddleProductRequest;
  }) => {
    const response = await putRequest(
      `${BASE_URL}/products/${productId}`,
      null,
      true,
      request,
      null,
      ServiceType.Payment
    );
    return { productId, ...response.data };
  }
);

export const addPriceToProduct = createAsyncThunk(
  "paddleProducts/addPrice",
  async ({
    productId,
    request,
  }: {
    productId: string;
    request: ICreatePaddlePriceRequest;
  }) => {
    const response = await postRequest(
      `${BASE_URL}/products/${productId}/prices`,
      null,
      true,
      request,
      null,
      ServiceType.Payment
    );
    return response.data as IPaddlePrice;
  }
);

export const getProductPrices = createAsyncThunk(
  "paddleProducts/getPrices",
  async (productId: string) => {
    const response = await getRequest(
      `${BASE_URL}/products/${productId}/prices`,
      null,
      true,
      ServiceType.Payment
    );
    return { productId, prices: response.data as IPaddlePrice[] };
  }
);

export const getFeatureGroups = createAsyncThunk(
  "paddleProducts/getFeatureGroups",
  async () => {
    const response = await getRequest(
      `${BASE_URL}/feature-groups`,
      null,
      true,
      ServiceType.Payment
    );
    return response.data as IFeatureGroup[];
  }
);

export const getProductEditions = createAsyncThunk(
  "paddleProducts/getProductEditions",
  async () => {
    const response = await getRequest(
      `${BASE_URL}/product-editions`,
      null,
      true,
      ServiceType.Payment
    );
    return response.data as IProductEdition[];
  }
);

export const updatePaddlePrice = createAsyncThunk(
  "paddleProducts/updatePrice",
  async ({
    productId,
    priceId,
    request,
  }: {
    productId: string;
    priceId: string;
    request: IUpdatePaddlePriceRequest;
  }) => {
    const response = await putRequest(
      `${BASE_URL}/products/${productId}/prices/${priceId}`,
      null,
      true,
      request,
      null,
      ServiceType.Payment
    );
    return response.data as IPaddlePrice;
  }
);

export const getPaddlePrice = createAsyncThunk(
  "paddleProducts/getPrice",
  async ({ productId, priceId }: { productId: string; priceId: string }) => {
    const response = await getRequest(
      `${BASE_URL}/products/${productId}/prices/${priceId}`,
      null,
      true,
      ServiceType.Payment
    );
    return response.data as IPaddlePrice;
  }
);

export const deletePaddlePrice = createAsyncThunk(
  "paddleProducts/deletePrice",
  async ({ productId, priceId }: { productId: string; priceId: string }) => {
    await putRequest(
      `${BASE_URL}/products/${productId}/prices/${priceId}`,
      null,
      true,
      { status: "archived" },
      null,
      ServiceType.Payment
    );
    return { productId, priceId };
  }
);

export const archivePaddleProduct = createAsyncThunk(
  "paddleProducts/archive",
  async (productId: string) => {
    const response = await putRequest(
      `${BASE_URL}/products/${productId}`,
      null,
      true,
      { status: "archived" },
      null,
      ServiceType.Payment
    );
    return { productId, ...response.data };
  }
);